import {Product} from "~/utils/Product";
import {Cart, type CartProduct} from "~/utils/Cart";
import {User} from "~/utils/User";
import {Article} from "~/utils/Article";
import {EventCategory} from "~/utils/EventCategory";

export type EventPackedArticle = {
    localId: string
    componentId: number | null
    article: Article | null
    product: Product | null
    name: string
    plannedQty: number
    packedQty: number
    checked: boolean
    comment: string
    sectionName: string
    fromOffer: boolean
    scannedCode: string
}

export type EventCableItem = {
    cableType: string
    lengthMeters: number | null
    quantity: number
    note: string
    forItem: string
    forQuantity: number | null
    confidence: string
}

export type EventCableSection = {
    name: string
    items: EventCableItem[]
}

export type EventCableList = {
    sections: EventCableSection[]
    warnings: string[]
    assumptions: string[]
}

export type EventCableListStatus = "idle" | "generated" | "failed"

export function eventDayKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
}

export function eventDayFromKey(key: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key)
    if (!match) return null
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    return Number.isNaN(date.getTime()) ? null : date
}

export function eventDayRange(startDate: Date | null, endDate: Date | null): string[] {
    if (!startDate && !endDate) return []
    const start = startOfEventDay(startDate ?? endDate!)
    const end = startOfEventDay(endDate ?? startDate!)
    const from = start <= end ? start : end
    const to = start <= end ? end : start
    const days: string[] = []
    const cursor = new Date(from)

    while (cursor <= to) {
        days.push(eventDayKey(cursor))
        cursor.setDate(cursor.getDate() + 1)
    }

    return days
}

export function compactEventDaysLabel(dayKeys: string[]): string {
    const dates = normalizeEventDayKeys(dayKeys)
        .map(eventDayFromKey)
        .filter((date): date is Date => !!date)

    if (!dates.length) return "-"
    if (dates.length === 1) return dates[0].toLocaleDateString("de-AT")
    if (dates.length > 5) {
        return `${dates.length} Tage zwischen ${dates[0].toLocaleDateString("de-AT")} und ${dates[dates.length - 1].toLocaleDateString("de-AT")}`
    }

    const grouped = new Map<string, Date[]>()
    for (const date of dates) {
        const key = `${date.getFullYear()}-${date.getMonth()}`
        grouped.set(key, [...(grouped.get(key) ?? []), date])
    }

    return Array.from(grouped.values()).map((group) => {
        const last = group[group.length - 1]
        const prefix = group.slice(0, -1).map((date) => `${String(date.getDate()).padStart(2, "0")}.`).join(", ")
        const suffix = last.toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric" })
        return prefix ? `${prefix}, ${suffix}` : suffix
    }).join(", ")
}

function normalizeEventDayKeys(dayKeys: string[]): string[] {
    return Array.from(new Set(dayKeys.filter((key) => eventDayFromKey(key)))).sort()
}

function startOfEventDay(date: Date): Date {
    const next = new Date(date)
    next.setHours(0, 0, 0, 0)
    return next
}

export class MyEvent {
    id = -1
    startDate:Date|null = null
    endDate:Date|null = null
    eventDays: string[] | null = null
    invoiceDate:Date|null = null
    paymentTarget:number = 14
    name = ""
    info = ""
    eventIsOver = false
    offerProducts: CartProduct[] = []
    packedArticleNumbers: number[] = []
    packedArticles: EventPackedArticle[] = []
    discount:number = 0;
    skonto:number = 0;
    user:User = new User()
    eventCategory: EventCategory | null = null
    offerNumber = ""
    invoiceNumber = ""
    showPrices = true
    innergemeinschaftlich = false
    vorauskasse = false
    status:"open"|"offered"|"confirmed"|"in_progress"|"waiting_for_payment"|"done"|"declined" = "open"
    generatedCableList: EventCableList | null = null
    cableListGeneratedAt = ""
    cableListStatus: EventCableListStatus = "idle"
    cableListPromptVersion = ""
    feedbackMailSent = false


    parseJson(ret: { id: number; attributes: { [key: string]: any } }) {
        this.id = ret.id;
        this.startDate = new Date(ret.attributes.Datum)
        this.endDate = ret.attributes.EndDatum ? new Date(ret.attributes.EndDatum) : this.startDate; //use startDate if no EndDatum
        this.eventDays = Array.isArray(ret.attributes.eventDays) ? normalizeEventDayKeys(ret.attributes.eventDays.map((day:any) => String(day))) : null
        this.invoiceDate = ret.attributes.invoiceDate ? new Date(ret.attributes.invoiceDate) : null;
        this.paymentTarget = ret.attributes.paymentTarget ? ret.attributes.paymentTarget : 14;
        this.name = ret.attributes.Name
        this.eventIsOver = ret.attributes.eventIsOver
        this.discount = ret.attributes.discount
        this.skonto = ret.attributes.skonto ?? 0
        this.offerNumber = ret.attributes.offerNumber ? ret.attributes.offerNumber : ""
        this.invoiceNumber = ret.attributes.invoiceNumber ? ret.attributes.invoiceNumber : ""
        this.showPrices = ret.attributes.showPrices
        this.innergemeinschaftlich = ret.attributes.innergemeinschaftlich ? ret.attributes.innergemeinschaftlich : false
        this.vorauskasse = ret.attributes.vorauskasse ? ret.attributes.vorauskasse : false
        this.status = ret.attributes.status ? ret.attributes.status : "open"
        this.info = ret.attributes.info ? ret.attributes.info : ""
        this.feedbackMailSent = ret.attributes.feedback_mail_sent ?? false
        if(ret.attributes.user){
            this.user.parseJson(ret.attributes.user.data)
        }
        if(ret.attributes.eventCategory?.data){
            const category = new EventCategory()
            category.parseJson(ret.attributes.eventCategory.data)
            this.eventCategory = category
        } else {
            this.eventCategory = null
        }
        if(ret.attributes.Angebot) {
            this.offerProducts = ret.attributes.Angebot.map((value:any): CartProduct|undefined => {
                if(value.product && value.product.data){
                    const tempProduct = new Product()
                    tempProduct.parseJson(value.product.data)
                    return {
                        editSectionName: false,
                        count: value.Anzahl,
                        product: tempProduct,
                        price: value.price?value.price:tempProduct.Price,
                        comment: value.comment?value.comment:"",
                        factor: (value.factor != null && value.factor != undefined)?value.factor:1.0,
                        sectionName: value.sectionName?value.sectionName:""
                    }
                }
            })
        }

        if(ret.attributes.PackedArticles){
            this.packedArticles = ret.attributes.PackedArticles.map((value:any, index:number) => {
                let product: Product | null = null
                let article: Article | null = null

                if (value.product?.data) {
                    product = new Product()
                    product.parseJson(value.product.data)
                }

                if (value.article?.data) {
                    article = new Article()
                    article.parseJson(value.article.data)
                    if (!product && article.product?.id > 0) {
                        product = article.product
                    }
                }

                const plannedQty = Math.max(Number(value.plannedQty ?? 0), 0)
                const packedQty = Math.max(Number(value.packedQty ?? 0), 0)
                const name = (value.name ?? "").trim() || product?.Name || article?.article_number || ""

                return {
                    localId: String(value.id ?? `${index}_${name || "packed"}`),
                    componentId: value.id ?? null,
                    article,
                    product,
                    name,
                    plannedQty,
                    packedQty,
                    checked: value.checked ?? packedQty >= Math.max(plannedQty, 1),
                    comment: value.comment ?? "",
                    sectionName: value.sectionName ?? "",
                    fromOffer: value.fromOffer ?? false,
                    scannedCode: value.scannedCode ?? "",
                }
            })
            this.packedArticleNumbers = ret.attributes.PackedArticles.map((value:any)=>{
                return value
            })
        } else {
            this.packedArticles = []
        }

        this.generatedCableList = this.parseGeneratedCableList(ret.attributes.generatedCableList)
        this.cableListGeneratedAt = ret.attributes.cableListGeneratedAt ? String(ret.attributes.cableListGeneratedAt) : ""
        this.cableListStatus = ret.attributes.cableListStatus ?? "idle"
        this.cableListPromptVersion = ret.attributes.cableListPromptVersion ?? ""
    }

    private parseGeneratedCableList(raw: any): EventCableList | null {
        if (!raw || typeof raw !== "object") {
            return null
        }

        const sections = Array.isArray(raw.sections) ? raw.sections.map((section: any) => ({
            name: String(section?.name ?? ""),
            items: Array.isArray(section?.items) ? section.items.map((item: any) => ({
                cableType: String(item?.cableType ?? item?.cable_type ?? ""),
                lengthMeters: item?.lengthMeters ?? item?.length_m ?? null,
                quantity: Number(item?.quantity ?? 0),
                note: String(item?.note ?? ""),
                forItem: String(item?.forItem ?? item?.for_item ?? ""),
                forQuantity: item?.forQuantity ?? item?.for_quantity ?? null,
                confidence: String(item?.confidence ?? ""),
            })) : [],
        })) : []

        return {
            sections,
            warnings: Array.isArray(raw.warnings) ? raw.warnings.map((entry: any) => String(entry)) : [],
            assumptions: Array.isArray(raw.assumptions) ? raw.assumptions.map((entry: any) => String(entry)) : [],
        }
    }
    toJSON () {
        const baseDate = this.startDate ?? new Date()
        const endDate = this.endDate ?? baseDate

        const retValue = {
            Datum: baseDate.toISOString(),
            EndDatum: endDate.toISOString(),
            eventDays: this.eventDays,
            invoiceDate: this.invoiceDate?.toISOString(),
            paymentTarget: this.paymentTarget,
            Name: this.name,
            eventIsOver: this.eventIsOver,
            discount: this.discount,
            skonto: this.skonto,
            offerNumber: this.offerNumber,
            invoiceNumber: this.invoiceNumber,
            showPrices: this.showPrices,
            innergemeinschaftlich: this.innergemeinschaftlich,
            vorauskasse: this.vorauskasse,
            Angebot: [] as {Anzahl:number, product:number, factor:number, price:number, comment:string,sectionName:string}[],
            user: this.user.id > 0 ? this.user.id : null,
            eventCategory: this.eventCategory?.id ? this.eventCategory.id : null,
            status: this.status,
            info: this.info,
            PackedArticles: [] as {
                id?: number,
                article?: number | null,
                product?: number | null,
                name: string,
                plannedQty: number,
                packedQty: number,
                checked: boolean,
                comment: string,
                sectionName: string,
                fromOffer: boolean,
                scannedCode: string,
            }[],
        } // here I make a POJO's copy of the class instance
        this.offerProducts.forEach((product)=>{
            retValue.Angebot.push({
                Anzahl:product.count,
                product:product.product.id,
                factor:product.factor,
                price:product.price,
                comment:product.comment,
                sectionName:product.sectionName,
            })
        })
        this.packedArticles.forEach((item) => {
            retValue.PackedArticles.push({
                ...(item.componentId ? { id: item.componentId } : {}),
                article: item.article?.id ?? null,
                product: item.product?.id ?? null,
                name: item.name,
                plannedQty: item.plannedQty,
                packedQty: item.packedQty,
                checked: item.checked,
                comment: item.comment,
                sectionName: item.sectionName,
                fromOffer: item.fromOffer,
                scannedCode: item.scannedCode,
            })
        })
        return retValue
    }

    getEffectiveEventDays(): string[] {
        if (Array.isArray(this.eventDays)) {
            const allowedDays = new Set(eventDayRange(this.startDate, this.endDate))
            return normalizeEventDayKeys(this.eventDays).filter((day) => allowedDays.has(day))
        }
        return eventDayRange(this.startDate, this.endDate)
    }

    getEventDaysLabel(): string {
        return compactEventDaysLabel(this.getEffectiveEventDays())
    }

    generateNewOfferInvoiceNumber(prefix:string){
        let today = new Date();
        let year = today.getFullYear().toString().slice(-2); // Die letzten zwei Ziffern des Jahres
        let month = ("0" + (today.getMonth() + 1)).slice(-2); // Der Monat (mit führender Null falls nötig)
        return `${prefix}${year}${month}001`
    }
/*

    toApiStruct(){
        const retValue:{Anzahl:number, product:{id:number}}[] = []
        this.products.forEach((product)=>{
            retValue.push({Anzahl:product.count, product:{id:product.product.id}})
        })
        return retValue
    }*/

    getNettoSum() {
        let sum = 0
        this.offerProducts.forEach((product:CartProduct)=>{
            if(product){
                sum += product.count*product.price*product.factor
            }
        })
        return sum
    }
    getNettoSumDiscounted(){
        return this.getNettoSum() * (1 - (this.discount/100))
    }
    getTotalMwSt(){
        return this.getNettoSumDiscounted()*0.2
    }
    getTotalSum(){
        return this.getNettoSumDiscounted()*1.2
    }
    getDueDate(): Date | null {
        if(this.invoiceDate){
            const dueDate = new Date(this.invoiceDate)
            dueDate.setDate(dueDate.getDate() + this.paymentTarget)
            return dueDate
        }
        return null
    }
    isOverdue(): number {
        const dueDate = this.getDueDate()
        if(dueDate){
            const today = new Date()
            const diffTime = today.getTime() - dueDate.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays
        }
        return 0
    }
}

export const eventStatusTypes = [
    "open",
    "offered",
    "confirmed",
    "in_progress",
    "waiting_for_payment",
    "done",
    "declined"
 ]
